import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  PERM_MOD_SETTINGS_PLANS,
  PLAN_PAGE,
  PLANS_OPERATIONS,
} from 'src/constants';
import { RouteContext } from 'src/context';
import history from 'src/history';
import { useCustomerSubscription } from 'src/hooks/useCustomerSubscription';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { AlertCircleIcon } from 'src/legacy/components/Icons';
import { AlertModal } from 'src/legacy/components/Modals';
import { useSettingsPageRedirection } from 'src/legacy/components/Settings/useSettingsPageRedirection';
import * as ReduxType from 'src/store/reduxTypes';
import * as UserUtils from 'src/utils/UserUtils';

type ModalData = {
  title: string;
} & (
  | { message: string; messageAdmin?: never; messageStaff?: never }
  | { message?: never; messageAdmin: string; messageStaff: string }
);

export const ChoosePlanAlertDialog: React.FC = () => {
  const { t } = useTranslation();

  const [planAlertModalData, setPlanAlertModalData] = useState<ModalData>({
    title: '',
    message: '',
  });

  const userLoaded = useSelector(
    (state: ReduxType.RootState) => state.user.loaded,
  );

  const { pathname } = React.useContext(RouteContext);
  const { stripeCustomer } = useCustomerSubscription();
  const { isTrialPeriodExpired, isCancelledPlan, isPaidPlan } = usePlanStatus();

  const permissions = useSelector(
    (state: ReduxType.RootState) => state.user.data?.permissions,
  );

  const enableBillingAction = UserUtils.hasOperation(
    PERM_MOD_SETTINGS_PLANS,
    PLANS_OPERATIONS.UpgradePlan,
    permissions,
  );

  const modalData = useMemo(
    () => ({
      noActivePlan: ({ isAdmin }: { isAdmin: boolean }) => ({
        title: t(($) => $.page_plans.plan_alert_modal.no_active_plan.title),
        message: t(
          ($) =>
            $.page_plans.plan_alert_modal.no_active_plan[
              isAdmin ? 'body_admin' : 'body_staff'
            ],
        ),
      }),
      trialExpired: ({ isAdmin }: { isAdmin: boolean }) => ({
        title: t(($) => $.page_plans.plan_alert_modal.trial_expired.title),
        message: t(
          ($) =>
            $.page_plans.plan_alert_modal.trial_expired[
              isAdmin ? 'body_admin' : 'body_staff'
            ],
        ),
      }),
    }),
    [t],
  );

  useEffect(() => {
    if (userLoaded) {
      // Hide plan alert modal
      // when stripe customer is not available
      // or settings page is opened
      if (!stripeCustomer || pathname.startsWith('/settings/')) {
        setPlanAlertModalData({ title: '', message: '' });
      } else if (isCancelledPlan && !isPaidPlan) {
        // when paid subscription is cancelled because of any reason
        // manual cancellation or non-payment cancellation
        if (enableBillingAction) {
          setPlanAlertModalData(modalData.noActivePlan({ isAdmin: true }));
        } else {
          setPlanAlertModalData(modalData.noActivePlan({ isAdmin: false }));
        }
      } else if (isTrialPeriodExpired && !isPaidPlan) {
        // when trial has expired
        if (enableBillingAction) {
          setPlanAlertModalData(modalData.trialExpired({ isAdmin: true }));
        } else {
          setPlanAlertModalData(modalData.trialExpired({ isAdmin: false }));
        }
      }
    }
  }, [
    stripeCustomer,
    userLoaded,
    isTrialPeriodExpired,
    isCancelledPlan,
    isPaidPlan,
    pathname,
    modalData,
    enableBillingAction,
    t,
  ]);

  const { goToSettings: goToPlanSettingsPage } = useSettingsPageRedirection(
    true,
    PLAN_PAGE.path,
  );

  const handleConfirm = () => {
    goToPlanSettingsPage();
  };

  const handleChangeWorkspace = () => {
    history.push('/add-account');
  };

  return (
    <AlertModal
      title={planAlertModalData.title}
      open={Boolean(planAlertModalData.title)}
      onSuccess={enableBillingAction ? handleConfirm : undefined}
      footerLeftButtonLabel="Switch workspace"
      onFooterLeftButtonClick={handleChangeWorkspace}
      successButtonLabel="Choose a plan"
      Icon={AlertCircleIcon}
    >
      {planAlertModalData.message}
    </AlertModal>
  );
};
